// @djsem/render-state — Content Generation / Render / Release Studio (Prompt 22R).
// PLANNING layer over the tested P22 ReleaseStudioService: content concepts, social clip
// specs, platform presets, campaign packs, and a release checklist. NO actual rendering,
// NO billing (P22's billed:false stays the only money-adjacent fact), rights aggregated
// via the same BLOCK:/warn convention.

import { ReleaseStudioService, type DJSEMReleaseExportJob, type DJSEMReleaseSourceRightsSnapshot } from "./releaseStudio";
import { SeqCounter } from "@djsem/shared";
import { type ValidationResult, type DJSEMValidationResult, toDJSEMValidationResult } from "@djsem/shared";

// ---- contracts --------------------------------------------------------------

/** The kind of release content a {@link DJSEMContentConcept} plans to produce. */
export type DJSEMContentConceptKind =
  | "artwork" | "teaser_video" | "social_clip" | "canvas_loop" | "youtube_full_visualizer" | "campaign_pack";

/** A named platform output preset (aspect ratio, max duration, looping) content can target. */
export interface DJSEMPlatformPreset {
  id: string; label: string;
  aspectRatio: "1:1" | "9:16" | "16:9" | "4:5";
  maxDurationSeconds?: number;
  loop: boolean;
  notes: string[];
}

/** A planned piece of release content: kind, source, platform target, and rights/warning state. */
export interface DJSEMContentConcept {
  id: string;
  kind: DJSEMContentConceptKind;
  title: string;
  sourceTrackId?: string;
  sourceSetId?: string;
  brandWorldId?: string;
  platformPresetId?: string;
  visualSceneIds: string[];
  typographyLaneId?: string;
  status: "draft" | "approved" | "rejected";
  rightsWarnings: string[];
  warnings: string[];
}

/** A grouped set of content concepts plus a release checklist, built by {@link ContentGenerationPlanningService.buildCampaignPack}. */
export interface DJSEMCampaignPack {
  id: string;
  title: string;
  conceptIds: string[];
  releaseChecklist: Array<{ item: string; done: boolean; required: boolean }>;
  warnings: string[];
}

/** Input for {@link ContentGenerationPlanningService.createContentConcept}. */
export interface CreateContentConceptInput {
  kind: DJSEMContentConceptKind; title: string;
  sourceTrackId?: string; sourceSetId?: string; brandWorldId?: string;
  platformPresetId?: string; visualSceneIds?: string[]; typographyLaneId?: string;
  rights?: DJSEMReleaseSourceRightsSnapshot;
}
/** Input for {@link ContentGenerationPlanningService.buildCampaignPack}. */
export interface BuildCampaignPackInput { title: string; concepts: DJSEMContentConcept[]; }
/** Input for {@link ContentGenerationPlanningService.toExportJob}. */
export interface ConceptToExportJobInput { concept: DJSEMContentConcept; snapshotId?: string; rights?: DJSEMReleaseSourceRightsSnapshot; createdAt?: string; }

const _seq = new SeqCounter();

// ---- platform presets (structural facts, not marketing claims) ----------------

/** Structural platform output presets (aspect ratio / duration / looping facts only). */
export const DJSEM_PLATFORM_PRESETS: DJSEMPlatformPreset[] = [
  { id: "preset_square", label: "Square post", aspectRatio: "1:1", maxDurationSeconds: 60, loop: false, notes: [] },
  { id: "preset_vertical_short", label: "Vertical short", aspectRatio: "9:16", maxDurationSeconds: 60, loop: false, notes: [] },
  { id: "preset_story", label: "Story", aspectRatio: "9:16", maxDurationSeconds: 15, loop: false, notes: [] },
  { id: "preset_canvas_loop", label: "Canvas loop", aspectRatio: "9:16", maxDurationSeconds: 8, loop: true, notes: ["Short seamless loop."] },
  { id: "preset_landscape_full", label: "Full-length visualizer", aspectRatio: "16:9", loop: false, notes: ["Full track duration."] },
  { id: "preset_portrait_feed", label: "Portrait feed", aspectRatio: "4:5", maxDurationSeconds: 60, loop: false, notes: [] },
];

// ---- service ----------------------------------------------------------------

/**
 * PLANNING layer over the tested Release Studio export service: content concepts, social
 * clip specs, platform presets, and campaign packs with a release checklist. No actual
 * rendering and no billing here — `billed: false` remains the only money-adjacent fact.
 */
export class ContentGenerationPlanningService {
  private release = new ReleaseStudioService();

  /**
   * Creates a draft content concept, collecting warnings for missing sources/unknown
   * presets and rights warnings from the supplied rights snapshot.
   * @param input - the concept's kind, title, source, platform preset, and optional rights.
   * @returns the new draft concept.
   */
  createContentConcept(input: CreateContentConceptInput): DJSEMContentConcept {
    const warnings: string[] = [];
    const rightsWarnings: string[] = [];
    if (!input.sourceTrackId && !input.sourceSetId) warnings.push("Concept has no source track or set; attach one before generation.");
    if (input.platformPresetId && !DJSEM_PLATFORM_PRESETS.some((p) => p.id === input.platformPresetId)) warnings.push(`Unknown platform preset ${input.platformPresetId}.`);
    if (input.rights) {
      for (const [k, v] of Object.entries(input.rights)) {
        if (v === "restricted") rightsWarnings.push(`BLOCK: ${k} restricted — blocked for public content.`);
        else if (v === "unknown") rightsWarnings.push(`${k} rights unknown; review before publishing.`);
      }
    }
    return {
      id: `concept_${_seq.next()}`, kind: input.kind, title: input.title,
      sourceTrackId: input.sourceTrackId, sourceSetId: input.sourceSetId,
      brandWorldId: input.brandWorldId, platformPresetId: input.platformPresetId,
      visualSceneIds: input.visualSceneIds ?? [], typographyLaneId: input.typographyLaneId,
      status: "draft", rightsWarnings, warnings,
    };
  }

  /**
   * Validates a content concept: id/title present, has a source, no blocking rights
   * warnings, and (for canvas loops) a looping platform preset.
   * @param c - the concept to validate.
   * @returns a validation result listing any issues.
   */
  validateConcept(c: DJSEMContentConcept): DJSEMValidationResult {
    const issues: ValidationResult["issues"] = [];
    if (!c?.id || !c?.title) issues.push({ path: "$", message: "id and title required" });
    if (!c?.sourceTrackId && !c?.sourceSetId) issues.push({ path: "$.source", message: "concept needs a source track or set" });
    for (const w of c?.rightsWarnings ?? []) if (w.startsWith("BLOCK:")) issues.push({ path: "$.rightsWarnings", message: w });
    // preset duration sanity for clip kinds
    const preset = DJSEM_PLATFORM_PRESETS.find((p) => p.id === c.platformPresetId);
    if (c.kind === "canvas_loop" && preset && !preset.loop) issues.push({ path: "$.platformPresetId", message: "canvas_loop requires a looping preset" });
    return toDJSEMValidationResult({ valid: issues.length === 0, issues }, "content_concept");
  }

  /**
   * Bridges a content concept into the tested Release Studio export job system (one export
   * system; no separate rendering pipeline).
   * @param input - the concept, an optional source snapshot id, rights, and creation timestamp.
   * @returns the created release export job.
   */
  toExportJob(input: ConceptToExportJobInput): DJSEMReleaseExportJob {
    const profile = input.concept.kind === "youtube_full_visualizer" || input.concept.kind === "teaser_video"
      ? "track_visual_preview"
      : "social_teaser";
    return this.release.createExportJob({
      profile,
      sourceSnapshotId: input.snapshotId,
      brandWorldId: input.concept.brandWorldId,
      visualSceneIds: input.concept.visualSceneIds,
      rights: input.rights,
      createdAt: input.createdAt,
    });
  }

  /**
   * Groups content concepts into a campaign pack with a release checklist gated by which
   * concept kinds are present.
   * @param input - the pack title and its member concepts.
   * @returns the assembled campaign pack.
   */
  buildCampaignPack(input: BuildCampaignPackInput): DJSEMCampaignPack {
    const warnings: string[] = [];
    const blocked = input.concepts.filter((c) => c.rightsWarnings.some((w) => w.startsWith("BLOCK:")));
    if (blocked.length) warnings.push(`${blocked.length} concept(s) have blocking rights issues and cannot ship publicly.`);
    const kinds = new Set(input.concepts.map((c) => c.kind));
    return {
      id: `pack_${_seq.next()}`, title: input.title,
      conceptIds: input.concepts.map((c) => c.id).sort(),
      releaseChecklist: [
        { item: "Artwork approved", done: false, required: true },
        { item: "Teaser video approved", done: false, required: kinds.has("teaser_video") },
        { item: "Social clips approved", done: false, required: kinds.has("social_clip") },
        { item: "Canvas loop approved", done: false, required: kinds.has("canvas_loop") },
        { item: "Rights review complete", done: false, required: true },
        { item: "Platform presets confirmed", done: false, required: true },
      ],
      warnings,
    };
  }
}
